import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Stack,
  Divider
} from '@mui/material'
import { Add, Edit, Delete } from '@mui/icons-material'
import { useNotify } from 'react-admin'
import { type PromoBadge } from '@repo/supabase'
import { supabase } from '../supabaseClient'
import { usePromoBadges } from '../hooks/usePromoBadges'

const DEFAULT_BG = '#ea580c'
const DEFAULT_TEXT = '#ffffff'

type RpcResult = { success: boolean; error?: string; id?: string }

interface BadgeFormState {
  label: string
  backgroundColor: string
  textColor: string
}

interface Props {
  open: boolean
  onClose: () => void
  /** Wołane po dodaniu/edycji/usunięciu — pozwala odświeżyć listę lokali (selektory kafelków). */
  onChanged?: () => void
}

export const PromoBadgeManagerDialog: React.FC<Props> = ({ open, onClose, onChanged }) => {
  const notify = useNotify()
  const { badges, moduleEnabled, tenantId, isLoaded, reload } = usePromoBadges()

  const [editorOpen, setEditorOpen] = useState(false)
  const [editingBadge, setEditingBadge] = useState<PromoBadge | null>(null)
  const [form, setForm] = useState<BadgeFormState>({ label: '', backgroundColor: DEFAULT_BG, textColor: DEFAULT_TEXT })
  const [saving, setSaving] = useState(false)

  const openCreate = () => {
    setEditingBadge(null)
    setForm({ label: '', backgroundColor: DEFAULT_BG, textColor: DEFAULT_TEXT })
    setEditorOpen(true)
  }

  const openEdit = (badge: PromoBadge) => {
    setEditingBadge(badge)
    setForm({ label: badge.label, backgroundColor: badge.background_color, textColor: badge.text_color })
    setEditorOpen(true)
  }

  const handleSave = async () => {
    if (!form.label.trim()) {
      notify('Podaj etykietę kafelka', { type: 'warning' })
      return
    }
    if (!tenantId) return

    setSaving(true)
    try {
      const { data } = editingBadge
        ? await supabase.rpc('update_promo_badge', {
            p_badge_id: editingBadge.id,
            p_label: form.label.trim(),
            p_background_color: form.backgroundColor,
            p_text_color: form.textColor
          })
        : await supabase.rpc('create_promo_badge', {
            p_tenant_id: tenantId,
            p_label: form.label.trim(),
            p_background_color: form.backgroundColor,
            p_text_color: form.textColor
          })

      const result = data as RpcResult | null
      if (!result || !result.success) {
        notify(result?.error ?? 'Nieznany błąd', { type: 'error' })
        return
      }

      notify(editingBadge ? 'Zaktualizowano kafelek' : 'Utworzono kafelek', { type: 'success' })
      setEditorOpen(false)
      reload()
      onChanged?.()
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (badge: PromoBadge) => {
    // Ile lokali używa tego kafelka — ostrzeż przed odpięciem
    const { count } = await supabase
      .from('properties')
      .select('id', { count: 'exact', head: true })
      .eq('promo_badge_id', badge.id)

    const usage = count ?? 0
    const warn = usage > 0
      ? `Usunąć kafelek "${badge.label}"? Jest przypisany do ${usage} ${usage === 1 ? 'lokalu' : 'lokali'} — ${usage === 1 ? 'wróci' : 'wrócą'} na domyślny PROMOCJA.`
      : `Usunąć kafelek "${badge.label}"?`
    if (!window.confirm(warn)) return

    const { data } = await supabase.rpc('delete_promo_badge', { p_badge_id: badge.id })
    const result = data as RpcResult | null
    if (!result || !result.success) {
      notify(result?.error ?? 'Nieznany błąd', { type: 'error' })
      return
    }
    notify(usage > 0 ? `Usunięto kafelek — ${usage} ${usage === 1 ? 'lokal wrócił' : 'lokali wróciło'} na domyślny` : 'Usunięto kafelek', { type: 'success' })
    reload()
    onChanged?.()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          Kafelki promocyjne
          {moduleEnabled && (
            <Button size="small" variant="contained" startIcon={<Add />} onClick={openCreate}>
              Dodaj kafelek
            </Button>
          )}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {!moduleEnabled ? (
          <Typography color="text.secondary">
            Moduł promocji nie jest włączony dla tego klienta.
          </Typography>
        ) : !isLoaded ? (
          <Typography color="text.secondary">Ładowanie...</Typography>
        ) : badges.length === 0 ? (
          <Typography color="text.secondary">
            Brak własnych kafelków. Domyślnie lokale pokazują kafelek „PROMOCJA". Dodaj własny, aby nadpisać etykietę i kolory.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {badges.map(badge => (
              <Box
                key={badge.id}
                sx={{
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2
                }}
              >
                <Chip
                  label={badge.label}
                  sx={{
                    backgroundColor: badge.background_color,
                    color: badge.text_color,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em'
                  }}
                />
                <Box>
                  <IconButton size="small" aria-label="Edytuj" onClick={() => openEdit(badge)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" aria-label="Usuń" onClick={() => handleDelete(badge)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Zamknij</Button>
      </DialogActions>

      {/* Edytor pojedynczego kafelka */}
      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingBadge ? 'Edytuj kafelek' : 'Nowy kafelek'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Etykieta"
              value={form.label}
              onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))}
              fullWidth
              autoFocus
              inputProps={{ maxLength: 40 }}
            />
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body2">Kolor tła</Typography>
              <input
                type="color"
                value={form.backgroundColor}
                onChange={(e) => setForm(f => ({ ...f, backgroundColor: e.target.value }))}
                style={{ width: 48, height: 36, border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer' }}
              />
            </Box>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body2">Kolor tekstu</Typography>
              <input
                type="color"
                value={form.textColor}
                onChange={(e) => setForm(f => ({ ...f, textColor: e.target.value }))}
                style={{ width: 48, height: 36, border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer' }}
              />
            </Box>
            <Divider />
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">Podgląd:</Typography>
              <Box
                sx={{
                  backgroundColor: form.backgroundColor,
                  color: form.textColor,
                  fontWeight: 700,
                  fontSize: '0.625rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  px: 1.25,
                  py: 0.5,
                  borderRadius: '20px'
                }}
              >
                {form.label || 'PROMOCJA'}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)}>Anuluj</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Zapisywanie...' : 'Zapisz'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}
